import { countBy, filter, omit } from 'lodash';

import {
  ContentInput,
  Network,
  S3Object,
  UrlMetadataImage,
  SharingChannelInfo,
  ContentFragment,
} from 'api';
import { AWS_REGION } from 'settings';

import { hasDisclosures, hasUrl } from 'features/content/helpers';
import {
  EMPTY_SLATE_EDITOR,
  FACEBOOK_LIMIT,
  INSTAGRAM_LIMIT,
  LINKEDIN_LIMIT,
  TWITTER_LIMIT,
  TWITTER_LIMIT_WITH_LINK,
} from 'app-constants';
import { GetPublicContentQuery } from 'api/public';
import { SPECIAL_CHARACTERS } from '../sharing/constants';

export const formatDisclosures = (content: ContentInput) => {
  if (!hasDisclosures(content)) {
    return [];
  }

  const disclosures = filter(content.disclosures, disclosure => !!disclosure.disclosure_id);
  return disclosures.map(item => omit(item, 'disclosure'));
};

export const convertUrlImageToPhoto = (image: UrlMetadataImage): S3Object => {
  return {
    bucket: image.bucket,
    key: image.file_key,
    width: image.width,
    height: image.height,
    region: AWS_REGION,
  };
};

export const foundReadOnlyGroup = (
  groupIds: string[],
  groupsRules: Record<string, { read_only: boolean }>
) => {
  let readOnlyGroupFound = false;

  if (groupIds.length === 0) {
    return readOnlyGroupFound;
  }

  groupIds.forEach(id => {
    if (groupsRules[id]?.read_only) {
      readOnlyGroupFound = true;
    }
  });

  return readOnlyGroupFound;
};

export const getSafeBody = (content: ContentInput) => {
  const { body } = content.content_object;
  return body === EMPTY_SLATE_EDITOR ? null : body;
};

export const generateRepostChannelsString = (
  channels: SharingChannelInfo[],
  repostText: string
) => {
  let channelListString = '';
  channels.forEach(channel => {
    if (channelListString === '') {
      channelListString += `#${channel.channel_name}`;
    } else {
      channelListString += `, ${channel.channel_name}`;
    }
  });
  channelListString = `${repostText} ${channelListString}`;

  return channelListString;
};

export const getCommentCharLimit = (
  network: Network,
  content: ContentFragment | GetPublicContentQuery['getPublicContent']
) => {
  let limit = 0;

  switch (network) {
    case 'twitter':
      limit = hasUrl(content) ? TWITTER_LIMIT_WITH_LINK : TWITTER_LIMIT;
      break;
    case 'linkedin':
      limit = LINKEDIN_LIMIT;
      break;
    case 'facebook':
      limit = FACEBOOK_LIMIT;
      break;
    case 'instagram':
      limit = INSTAGRAM_LIMIT;
      break;
  }

  return limit;
};

export const getTextLength = (network: Network, text: string) => {
  if (network !== Network.Linkedin) {
    return text.length;
  }

  function count(str: string, char: string) {
    return countBy(str)[char] || 0;
  }

  let specialCharacterCount = 0;
  SPECIAL_CHARACTERS.forEach(ch => {
    specialCharacterCount += count(text, ch);
  });

  return text.length + specialCharacterCount;
};
